import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { RecordLink } from './RecordLink';
import { Money, Row, day } from './bodyParts';

export interface PurchaseOrderDetail {
  id: string;
  orderNumber: string;
  status: string;
  orderDate: string;
  subtotal: number | string;
  taxAmount: number | string;
  discountAmount: number | string;
  totalAmount: number | string;
  paidAmount: number | string;
  notes?: string | null;
  supplier?: { name?: string | null } | null;
  items: Array<{ id: string; productName: string; orderedQty: number; receivedQty: number; unitCost: number | string; totalAmount: number | string }>;
}

interface PaymentRow { id: string; paymentNumber: string; amount: number | string }

/** Read-only purchase order for the record viewer (POS-I238). */
export function PurchaseOrderDetailBody({ data }: { data: PurchaseOrderDetail }) {
  const { t } = useTranslation('records');
  const payments = useQuery({
    queryKey: ['record-viewer', 'po-payments', data.id],
    queryFn: async () =>
      (await api.get<{ data: PaymentRow[] }>('/vendor-payments', { params: { purchaseOrderId: data.id, limit: 20 } })).data.data,
    retry: false,
  });
  return (
    <div className="space-y-5 text-sm">
      <dl className="space-y-1.5">
        <Row label={t('rvDate')}><span dir="ltr">{day(data.orderDate)}</span></Row>
        {data.supplier?.name && <Row label={t('rvSupplier')}>{data.supplier.name}</Row>}
        {data.notes && <Row label={t('rvNotes')}>{data.notes}</Row>}
      </dl>
      {data.items.length === 0 ? (
        <p className="text-muted-foreground">{t('rvNoItems')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-1 text-start font-medium">{t('rvProduct')}</th>
                <th className="py-1 text-end font-medium">{t('rvOrdered')}</th>
                <th className="py-1 text-end font-medium">{t('rvReceived')}</th>
                <th className="py-1 text-end font-medium">{t('rvLineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id} className="border-b align-top last:border-0">
                  <td className="py-1.5 break-words">{i.productName}</td>
                  <td className="py-1.5 text-end tabular-nums" dir="ltr">{i.orderedQty}</td>
                  <td className="py-1.5 text-end tabular-nums" dir="ltr">{i.receivedQty}</td>
                  <td className="py-1.5 text-end"><Money value={i.totalAmount} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <dl className="space-y-1.5 border-t pt-3">
        <Row label={t('rvSubtotal')}><Money value={data.subtotal} /></Row>
        {Number(data.discountAmount) > 0 && <Row label={t('rvDiscount')}><Money value={data.discountAmount} /></Row>}
        <Row label={t('rvTax')}><Money value={data.taxAmount} /></Row>
        <div className="font-semibold"><Row label={t('rvGrandTotal')}><Money value={data.totalAmount} /></Row></div>
        <Row label={t('rvPaid')}><Money value={data.paidAmount} /></Row>
      </dl>
      {payments.data && payments.data.length > 0 && (
        <dl className="space-y-1.5" data-testid="record-viewer-source">
          <dt className="text-xs font-medium text-muted-foreground">{t('rvVendorPayments')}</dt>
          {payments.data.map((p) => (
            <Row key={p.id} label={t('rvAmount')}>
              <RecordLink type="vendorPayment" id={p.id} label={p.paymentNumber} />
              <span className="ms-2"><Money value={p.amount} /></span>
            </Row>
          ))}
        </dl>
      )}
    </div>
  );
}
